// Imported from the module, not the ../utils barrel: the barrel also re-exports _browser and
// _screen (detect-browser, body-scroll-lock), which pulled ~24 KB of transpiled dependencies
// into the small head bundle that registers the custom elements (js/elements.js).
import createElement from "../utils/_createElement";

// Module-level const rather than a `#private` field: this class is bundled into js/elements.js,
// which is loaded blocking in <head>, and babel transpiles private class members into core-js
// WeakMap/WeakSet (~20 KB) for the configured browser targets.
const ROOT_STYLES = `
	:host {
		display: block;
	}
	:host *,
	:host *::before,
	:host *::after {
		box-sizing: inherit;
  }`;

export default class extends HTMLElement {
  _create() {
    if (this._initialized) {
      // Re-connected (an ancestor moved us into its own wrapper): disconnectedCallback nulled
      // __comp, so restore it rather than leaving the element half-initialized.
      if (!this.__comp) this._link();
      return;
    }
    this._initialized = true;

    // Kentico Page Builder re-renders/re-parents editable-area DOM after this element upgrades.
    // A shadow root with only <slot name="content"> drops any re-injected, un-slotted widget-zone
    // markup, leaving the section blank in Edit/Preview. In the editor, stay in the light DOM so
    // Kentico can manage the zones; SCSS selectors are light-DOM and styling is preserved.
    if (document.documentElement.classList.contains("kentico-page-builder")) {
      this.__comp = this;
      return;
    }

    const slot = createElement({
      tag: "slot",
      attributes: { name: "content" }
    });
    const styles = createElement({ tag: "style", text: ROOT_STYLES });
    const shadow = this.attachShadow({ mode: "open" });

    shadow.appendChild(styles);
    shadow.appendChild(slot);

    this._content = createElement({
      tag: "span",
      attributes: { slot: "content" }
    });
    this.appendChild(this._content);
    this._absorb();

    // js/elements.js registers these elements from <head>, so connectedCallback runs the moment
    // the parser sees the start tag — this.children is still empty at that point, and anything
    // parsed afterwards would be appended to the host, outside the named slot, and never render.
    // Absorbing on each mutation keeps up with the parser: MutationObserver callbacks are
    // microtasks, so they drain before the browser paints whatever the parser just produced.
    // The result is that the first paint already sees the final structure, which is the whole
    // point of upgrading early — no re-parenting after paint means no layout shift.
    if (document.readyState === "loading") {
      this._observer = new MutationObserver(() => this._absorb());
      this._observer.observe(this, { childList: true });
      document.addEventListener("DOMContentLoaded", () => this._settle(), { once: true });
    }
  }

  // Moves every element child into the slotted wrapper. Walks the whole child list rather than
  // just what follows the wrapper, because the wrapper is the first child while parsing and the
  // last one when the element upgrades after the document is complete.
  _absorb() {
    const content = this._content;
    let node = this.firstChild;

    while (node) {
      const next = node.nextSibling;
      // Elements only, matching the original [...this.children]: stray text nodes stay in the
      // host, where they are un-slotted and therefore not rendered, exactly as before.
      if (node !== content && node.nodeType === 1) {
        content.appendChild(node);
      }
      node = next;
    }

    this._link();
  }

  // What the ResizeObserver/IntersectionObserver callbacks in _component.js put attributes on.
  // Overridden by Component, which is its own __comp.
  _link() {
    this.__comp = this._content ? this._content.querySelector(":scope > *") : this;
  }

  _settle() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    this._absorb();
  }

  constructor() {
    super();
    // DOM manipulation must not happen in the constructor (Custom Elements spec).
    // Moved to connectedCallback to fix NotSupportedError in Kentico Page Builder editor.
  }

  connectedCallback() {
    this._create();
  }

  disconnectedCallback() {
    this.__comp = null;
  }
}
